import { Grid, Drawer } from "@mui/material";
import toast from "react-hot-toast";
import { useEffect, useState, useContext, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import CategoriesNavebar from "../../utils/CategoriesNavebar/CategoriesNavebar";
import styles from "./styles.module.css";
import ItemView from "components/utils/ItemView/ItemView";
import Cart from "components/utils/Cart/Cart";
import ProductContext from "context/ProductContext";
import SaleEditDialog from "components/popups/SaleEditDialog/SaleEditDialog";
import SalesContext from "context/SalesContext";
import ItemDetailsDialog from "components/popups/ItemDetailsDialog/ItemDetailsDialog";
import ItemOptionsDialog from "components/popups/ItemOptionsDialog/ItemOptionsDialog";
import SearchContext from "context/SearchContext";
import { ROUTES, STORAGE_KEYS, SEARCH_ACTION_TYPES, PRODUCT_TYPE, DEFAULT_PRODUCT_IMAGE, EVENTS } from "const";
import { eventsEmitter } from "utils/events";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import * as TerminalHelper from "helpers/TermialHelper";
import * as SalesHelper from "helpers/SalesHelper";
import CustomerSelector from "components/utils/CustomerSelector/CustomerSelector";
import BarcodeScanner from "components/utils/BarcodeScanner/BarcodeScanner";

function Sales() {
  const {
    selectedCategory,
    setSelectedCategory,
    availableItems,
    availableCategories,
    isLoading: loadingProductContext,
    charges,
  } = useContext(ProductContext);
  const { addProductToActiveSale, activeSale, setActiveSale, handleChangeSelectedCustomer } = useContext(SalesContext);
  const { searchTerm, searchAction, clearSearchAction } = useContext(SearchContext);
  const navigate = useNavigate();

  const lastProcessedActionRef = useRef(null);
  const gridRef = useRef(null);
  const autoSelectedCategoryId = useRef(null);

  const [selectedItems, setSelectdedItems] = useState([]);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [invoiceEdit, setInvoiceEdit] = useState(false);
  const [selectedItemForDialog, setSelectedItemForDialog] = useState(null);
  const [selectedItemForOptions, setSelectedItemForOptions] = useState(null);

  const [cartOpen, setCartOpen] = useState(false);
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);
  const [orderTypes, setOrderTypes] = useState([]);

  useEffect(() => {
    try {
      const normalized = TerminalHelper.loadOrderTypesFromStorage() || [];
      setOrderTypes(normalized);
    } catch (err) {
      console.error("Failed to load order types:", err);
    }
  }, []);

  const totalItemsCount = useMemo(() => {
    return activeSale?.items?.reduce((sum, item) => sum + (item.qty || 0), 0) || 0;
  }, [activeSale?.items]);

  const handleOrderTypeChange = async (orderType) => {
    const nextOrderType = activeSale?.order_type === orderType ? null : orderType;

    try {
      const updatedSale = await SalesHelper.buildUpdatedSale({
        sale: activeSale,
        paymentModes: activeSale?.payment_modes || [],
        balancePayments: [],
        selectedCustomer: activeSale?.customer || null,
        discount: activeSale?.extraDiscount || 0,
        due: activeSale?.payable || 0,
        change: activeSale?.change || 0,
        shift: activeSale?.shift || null,
        orderType: nextOrderType,
        charges,
      });

      await SalesHelper.saveSaleInDB(updatedSale, { notify: true });

      setActiveSale(updatedSale);
    } catch (err) {
      console.error("Failed to update order type:", err);
      toast.error("Failed to update order type");
    }
  };

  useEffect(() => {
    const last = sessionStorage.getItem(STORAGE_KEYS.LAST_ROUTE);

    const handlePopState = (e) => {
      if (last === ROUTES.TABLE_DASHBOARD) {
        e.preventDefault();
        navigate(ROUTES.TABLE_DASHBOARD, { replace: true });
      }
    };

    const unsubscribeFn = eventsEmitter.on(EVENTS.POP_STATE, handlePopState);
    return unsubscribeFn;
  }, [navigate]);

  const openItemDetails = (item) => {
    setSelectedItemForDialog(item);
  };

  const closeItemDetails = () => setSelectedItemForDialog(null);

  const handleItemAdd = (product) => {
    if (product?.config?.product_options && product.config.product_options.length > 0) {
      setSelectedItemForOptions(product);
    } else {
      addProductToActiveSale(product);
    }
  };

  const reorderedCategories = useMemo(() => {
    if (!searchTerm) return availableCategories;

    const firstMatch = availableItems.find((item) => item?.name?.toLowerCase().includes(searchTerm.toLowerCase()));

    if (!firstMatch) return availableCategories;

    const matchCategoryId = firstMatch.category;

    const selectedCat = availableCategories.find((c) => c.id === matchCategoryId);
    if (!selectedCat) return availableCategories;

    return [selectedCat, ...availableCategories.filter((cat) => cat.id !== matchCategoryId)];
  }, [availableCategories, availableItems, searchTerm]);

  useEffect(() => {
    let filtered = availableItems;

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const contains = [];

      availableItems.forEach((item) => {
        const name = (item?.name || "").toLowerCase();
        if (name.includes(searchLower)) {
          contains.push(item);
        }
      });

      filtered = [...contains];

      if (filtered.length > 0 && selectedCategory?.id !== filtered[0].category) {
        const cat = availableCategories.find((c) => c.id === filtered[0].category);
        if (cat) {
          setSelectedCategory(cat);
          autoSelectedCategoryId.current = cat.id;
        }
      }
      setSelectdedItems(filtered);
      return;
    }

    if (autoSelectedCategoryId.current && selectedCategory?.id === autoSelectedCategoryId.current) {
      if (availableCategories.length > 0 && selectedCategory?.id !== availableCategories[0].id) {
        setSelectedCategory(availableCategories[0]);
      }
    }
    autoSelectedCategoryId.current = null;

    if (selectedCategory) {
      filtered = filtered.filter((item) => item?.category === selectedCategory.id);
    }

    setSelectdedItems(filtered);
  }, [availableCategories, selectedCategory, setSelectedCategory, availableItems, searchTerm]);

  useEffect(() => {
    if (searchTerm && selectedItems.length > 0) {
      setFocusedIndex(0);
    } else {
      setFocusedIndex(-1);
    }
  }, [searchTerm, selectedItems]);

  function computeGridColumnsCount(gridRef) {
    if (!gridRef?.current || gridRef.current.children.length === 0) return 1;
    const children = Array.from(gridRef.current.children);
    const firstChildOffsetTop = children[0].offsetTop;
    let count = 0;
    for (const child of children) {
      if (child.offsetTop === firstChildOffsetTop) {
        count++;
      } else {
        break;
      }
    }
    return count || 1;
  }

  function handleSearchActionKeyNavigation(
    searchAction,
    selectedItems,
    focusedIndex,
    columns,
    addProductToActiveSale,
    setFocusedIndex,
  ) {
    const { type } = searchAction;
    if (type === SEARCH_ACTION_TYPES.ARROW_DOWN) {
      setFocusedIndex((prev) => (prev + columns < selectedItems.length ? prev + columns : prev));
    } else if (type === SEARCH_ACTION_TYPES.ARROW_UP) {
      setFocusedIndex((prev) => (prev - columns >= 0 ? prev - columns : prev));
    } else if (type === SEARCH_ACTION_TYPES.ARROW_LEFT) {
      setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (type === SEARCH_ACTION_TYPES.ARROW_RIGHT) {
      setFocusedIndex((prev) => (prev + 1 < selectedItems.length ? prev + 1 : prev));
    } else if (type === SEARCH_ACTION_TYPES.ENTER) {
      const indexToAdd = focusedIndex === -1 ? 0 : focusedIndex;
      const itemToAdd = selectedItems[indexToAdd];
      if (itemToAdd) {
        const terminalSettings = TerminalHelper.getTerminalSettings();
        const allowNegativeStock = terminalSettings?.preventNegativeStock === "allow_negative_stock";

        if (itemToAdd?.type === PRODUCT_TYPE.SERVICE || (itemToAdd.stock ?? 0) > 0 || allowNegativeStock) {
          addProductToActiveSale(itemToAdd);
        } else {
          toast.error("No stock for this product");
        }
      }
    }
  }

  useEffect(() => {
    if (!searchAction || selectedItems.length === 0) return;
    if (lastProcessedActionRef.current === searchAction.timestamp) return;

    lastProcessedActionRef.current = searchAction.timestamp;
    const columns = computeGridColumnsCount(gridRef);

    if (
      [
        SEARCH_ACTION_TYPES.ARROW_DOWN,
        SEARCH_ACTION_TYPES.ARROW_UP,
        SEARCH_ACTION_TYPES.ARROW_LEFT,
        SEARCH_ACTION_TYPES.ARROW_RIGHT,
        SEARCH_ACTION_TYPES.ENTER,
      ].includes(searchAction.type)
    ) {
      handleSearchActionKeyNavigation(
        searchAction,
        selectedItems,
        focusedIndex,
        columns,
        addProductToActiveSale,
        setFocusedIndex,
      );
      clearSearchAction();
    }
  }, [searchAction, selectedItems, addProductToActiveSale, focusedIndex, clearSearchAction]);

  const productsInCartIds = useMemo(
    () => activeSale?.items?.map((i) => Number(i.id || i.productId)) || [],
    [activeSale.items],
  );

  const handleItemAction = (item, type) => {
    if (type === "add") {
      const terminalSettings = TerminalHelper.getTerminalSettings();
      const allowNegativeStock = terminalSettings?.preventNegativeStock === "allow_negative_stock";

      if (item?.type !== PRODUCT_TYPE.SERVICE && (item?.stock ?? 0) <= 0 && !allowNegativeStock) {
        toast.error("No stock for this product");
        return;
      }
      handleItemAdd(item);
    } else {
      openItemDetails(item);
    }
  };

  return (
    <>
      <Grid className={styles.pageWrap} item xs={12}>
        <Grid item xs={12} sx={{ height: "100%" }}>
          <Grid item className={`${styles.salesLayout} ${totalItemsCount > 0 ? styles.hasBottomBar : ""}`}>
            <Grid item className={styles.categoriesSidebar} xs={12} md="auto" lg="auto">
              <CategoriesNavebar categories={reorderedCategories} isloading={loadingProductContext} />
            </Grid>

            <Grid item className={styles.contentArea} xs={12} md>
              <Grid className={styles.itemListWrapper} padding={"0"}>
                {loadingProductContext ? (
                  <Grid item xs={12}>
                    <SkeletonLoading isOpen={loadingProductContext} height={96} count={9} />
                  </Grid>
                ) : (
                  <div className={styles.itemsGrid} ref={gridRef}>
                    {!selectedItems || selectedItems.length === 0 ? (
                      <Grid item xs={12} className={styles.noItemsWrapper}>
                        <div className={styles.noItemsMessage}>No availableItems in this category.</div>
                      </Grid>
                    ) : (
                      selectedItems.map((item, idx) => {
                        const keyVal = item.id;
                        const isInCart = productsInCartIds.includes(Number(item.id));

                        const isFocused = idx === focusedIndex;
                        return (
                          <div
                            className={`${styles.itemWrapper} ${isInCart ? styles.itemSelected : ""} ${
                              isFocused ? styles.itemFocused : ""
                            }`}
                            key={keyVal}
                          >
                            <ItemView item={item} onAction={handleItemAction} isInCart={isInCart} />
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </Grid>
            </Grid>

            <Grid className={styles.cart} item xs={12}>
              <Cart
                invoiceEditHandler={() => setInvoiceEdit(true)}
                onItemClick={(item) => setSelectedItemForDialog(item)}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {invoiceEdit && <SaleEditDialog handleClose={() => setInvoiceEdit(false)} saleLocalId={activeSale?.local_id} />}

      {selectedItemForDialog && (
        <ItemDetailsDialog open={!!selectedItemForDialog} saleItem={selectedItemForDialog} onClose={closeItemDetails} />
      )}

      {selectedItemForOptions && (
        <ItemOptionsDialog
          open={!!selectedItemForOptions}
          item={selectedItemForOptions}
          onClose={() => setSelectedItemForOptions(null)}
          onAdd={addProductToActiveSale}
        />
      )}

      {totalItemsCount > 0 && (
        <div className={styles.bottomBar}>
          <div className={styles.bottomBarRow1}>
            {activeSale?.customer ? (
              <button className={styles.customerSelectedBtn} onClick={() => handleChangeSelectedCustomer(null)}>
                {activeSale.customer.name}
              </button>
            ) : (
              <button className={styles.customerBtn} onClick={() => setShowCustomerSelector((prev) => !prev)}>
                Add Customer
              </button>
            )}

            {orderTypes.map((type) => (
              <button
                key={type}
                className={`${styles.orderTypeBtn} ${activeSale?.order_type === type ? styles.activeOrderTypeBtn : ""}`}
                onClick={() => handleOrderTypeChange(type)}
              >
                {type}
              </button>
            ))}
          </div>

          <div className={styles.bottomBarRow2}>
            <div className={styles.cartSummarySection} onClick={() => setCartOpen(true)}>
              <div className={styles.thumbnailContainer}>
                {activeSale?.items?.slice(0, 2).map((item, index) => {
                  const imgUrl = item?.item?.image || item?.image || DEFAULT_PRODUCT_IMAGE;
                  return <img key={index} src={imgUrl} alt={item?.name || "item"} className={styles.cartThumb} />;
                })}
              </div>
              <div className={styles.summaryText}>
                <h4 className={styles.summaryTitle}>
                  {totalItemsCount} Item{totalItemsCount !== 1 ? "s" : ""} · ₹{activeSale?.payable || 0}
                </h4>
                <p className={styles.summarySubtitle}>{activeSale?.order_type || "No Order Type"}</p>
              </div>
            </div>

            <button
              className={styles.payBillBtn}
              onClick={() => {
                if (!(activeSale?.items?.length || 0)) {
                  toast.error("Empty cart");
                  return;
                }
                setInvoiceEdit(true);
              }}
            >
              Pay Bill
            </button>
          </div>
        </div>
      )}

      <Drawer
        anchor="bottom"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: {
            height: "85vh",
            borderTopLeftRadius: "16px",
            borderTopRightRadius: "16px",
            padding: "16px 8px 8px 8px",
          },
        }}
      >
        <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
              padding: "0 8px",
            }}
          >
            <h3 style={{ margin: 0, fontWeight: 700 }}>Cart</h3>
            <button
              onClick={() => setCartOpen(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "20px",
                fontWeight: "bold",
                cursor: "pointer",
                color: "#666",
              }}
            >
              ✕
            </button>
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            <Cart
              invoiceEditHandler={() => {
                setCartOpen(false);
                setInvoiceEdit(true);
              }}
              onItemClick={(item) => {
                setSelectedItemForDialog(item);
              }}
            />
          </div>
        </div>
      </Drawer>

      {/* Customer Selector Popup for Mobile */}
      {showCustomerSelector && (
        <div className={styles.customerPopupMobile}>
          <CustomerSelector
            open={showCustomerSelector}
            onClose={() => setShowCustomerSelector(false)}
            onSelect={(customer) => {
              handleChangeSelectedCustomer(customer);
              setShowCustomerSelector(false);
            }}
          />
        </div>
      )}

      <BarcodeScanner
        isDisabled={
          invoiceEdit || Boolean(selectedItemForDialog) || Boolean(selectedItemForOptions) || showCustomerSelector
        }
      />
    </>
  );
}

export default Sales;
